/**
 * Estrategias concretas del recorder. **Browser-only** — usan
 * `MediaRecorder`, `AudioContext`, `Blob`. Excluido del coverage en
 * `vitest.config.ts` porque su validación real ocurre en F1 contra
 * Safari iOS / Chrome Android (`04_ARQUITECTURA.md` §4.1.1, §4.1.2).
 */

import type { RecorderStrategy } from './audioRecorder.js';

const NATIVE_MIME_TYPE = 'audio/webm;codecs=opus';

export function defaultIsNativeOpusSupported(): boolean {
  if (typeof MediaRecorder === 'undefined') return false;
  return MediaRecorder.isTypeSupported(NATIVE_MIME_TYPE);
}

export function defaultNativeStrategyFactory(stream: MediaStream): RecorderStrategy {
  const chunks: BlobPart[] = [];
  const recorder = new MediaRecorder(stream, { mimeType: NATIVE_MIME_TYPE });

  let stopResolve: (() => void) | null = null;
  let stopReject: ((err: unknown) => void) | null = null;

  recorder.addEventListener('dataavailable', (ev: BlobEvent) => {
    if (ev.data.size > 0) chunks.push(ev.data);
  });
  recorder.addEventListener('error', (ev: Event) => {
    const err = (ev as unknown as { error?: unknown }).error ?? ev;
    if (stopReject) stopReject(err);
  });
  recorder.addEventListener('stop', () => {
    if (stopResolve) stopResolve();
  });

  return {
    async start() {
      recorder.start();
    },
    async stop() {
      const stopped = new Promise<void>((resolve, reject) => {
        stopResolve = resolve;
        stopReject = reject;
      });
      recorder.stop();
      await stopped;

      // El `type` que viaja en `mime_type` es el que el navegador dice
      // haber grabado, no el que se pidió.
      const mimeType = recorder.mimeType || NATIVE_MIME_TYPE;
      const blob = new Blob(chunks, { type: mimeType });
      const binary = await blob.arrayBuffer();
      return { binary, mimeType: blob.type };
    },
    dispose() {
      stopResolve = null;
      stopReject = null;
      chunks.length = 0;
    },
  };
}

/**
 * Subset de la API de `opus-recorder` que el polyfill usa. El paquete real
 * no se importa estáticamente — se carga vía `import()` con módulo
 * resuelto en runtime, así `tsc --noEmit` no falla cuando el paquete no
 * está instalado todavía (la instalación efectiva ocurre en F1, cuando
 * `apps/web` integre la captura real). Si los tipos del paquete real
 * divergen, este shim limita la superficie de drift.
 */
interface OpusRecorderInstance {
  ondataavailable: ((typedArray: Uint8Array) => void) | null;
  onstop: (() => void) | null;
  onerror: ((event: Event) => void) | null;
  start(sourceNode?: AudioNode): Promise<void>;
  stop(): Promise<void>;
  close(): void;
}
interface OpusRecorderModule {
  default: new (options?: {
    encoderApplication?: number;
    numberOfChannels?: number;
  }) => OpusRecorderInstance;
}

export async function defaultPolyfillStrategyFactory(
  stream: MediaStream,
): Promise<RecorderStrategy> {
  // Indirección por variable: `tsc` no resuelve dynamic imports con string
  // no-literal a tiempo de compilación, así que apps/web puede typecheck-ear
  // sin tener `opus-recorder` instalado. F1 lo añade como dependencia
  // efectiva.
  const moduleName = 'opus-recorder';
  const mod = (await import(moduleName)) as OpusRecorderModule;
  const Recorder = mod.default;
  const chunks: Uint8Array[] = [];
  const recorder: OpusRecorderInstance = new Recorder({
    encoderApplication: 2049,
    numberOfChannels: 1,
  });

  let stopResolve: (() => void) | null = null;
  let stopReject: ((err: unknown) => void) | null = null;

  recorder.ondataavailable = (typedArray: Uint8Array) => {
    chunks.push(typedArray);
  };
  recorder.onstop = () => {
    if (stopResolve) stopResolve();
  };
  recorder.onerror = (event: Event) => {
    if (stopReject) stopReject(event);
  };

  return {
    async start() {
      const ctx = new AudioContext();
      const sourceNode = ctx.createMediaStreamSource(stream);
      await recorder.start(sourceNode);
    },
    async stop() {
      const stopped = new Promise<void>((resolve, reject) => {
        stopResolve = resolve;
        stopReject = reject;
      });
      await recorder.stop();
      await stopped;

      const total = chunks.reduce((sum, c) => sum + c.byteLength, 0);
      const merged = new Uint8Array(total);
      let offset = 0;
      for (const chunk of chunks) {
        merged.set(chunk, offset);
        offset += chunk.byteLength;
      }
      const binary = merged.buffer.slice(merged.byteOffset, merged.byteOffset + merged.byteLength);
      // `opus-recorder` produce Ogg. No cumple un uplink declarado
      // `audio/webm`; `verificarAudioDeclarado` lo ataja antes del ticket.
      return { binary, mimeType: 'audio/ogg' };
    },
    dispose() {
      stopResolve = null;
      stopReject = null;
      chunks.length = 0;
      recorder.close();
    },
  };
}
